"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { locales, localeNames, type Locale } from "@/i18n/config";
import { setUserLocale } from "@/i18n/actions";
import { Globe, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export function LanguageSwitcher({ className }: { className?: string }) {
  const locale = useLocale() as Locale;
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);

  const handleLocaleChange = (newLocale: Locale) => {
    startTransition(async () => {
      await setUserLocale(newLocale);
      router.refresh();
    });
    setIsOpen(false);
  };

  return (
    <div className={cn("relative", className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isPending}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg",
          "bg-card/50 border border-border/50 hover:bg-card hover:border-border",
          "text-sm font-medium text-foreground/80 hover:text-foreground",
          "transition-all duration-200",
          isPending && "opacity-50 cursor-wait"
        )}
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="h-4 w-4" />
        <span className="hidden sm:inline">{localeNames[locale]}</span>
        <span className="sm:hidden uppercase">{locale}</span>
        <ChevronDown className={cn(
          "h-3 w-3 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close on outside click */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Dropdown menu */}
          <div 
            className={cn(
              "absolute right-0 top-full mt-2 z-50",
              "min-w-[140px] py-1 rounded-lg",
              "bg-card border border-border shadow-lg",
              "animate-in fade-in-0 zoom-in-95 duration-150"
            )}
            role="listbox"
            aria-label="Available languages"
          >
            {locales.map((loc) => (
              <button
                key={loc}
                onClick={() => handleLocaleChange(loc)}
                className={cn(
                  "w-full flex items-center justify-between gap-3 px-4 py-2.5",
                  "text-sm text-left transition-colors",
                  loc === locale 
                    ? "bg-accent-primary/10 text-accent-primary" 
                    : "text-foreground/80 hover:bg-muted hover:text-foreground"
                )}
                role="option"
                aria-selected={loc === locale}
              >
                <span>{localeNames[loc]}</span>
                {loc === locale && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
